import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'app-delete-data-rentascr',
  templateUrl: './delete-data-rentascr.component.html',
  styleUrls: ['./delete-data-rentascr.component.scss']
})
export class DeleteDataRentascrComponent implements OnInit {
  // Paso actual del asistente (1: Credenciales, 2: Código de 6 dígitos, 3: Confirmación de borrado)
  step: number = 1;
  
  // Campos del formulario
  email: string = '';
  password: string = '';
  codeDigits: string[] = ['', '', '', '', '', ''];
  
  // Estados de la interfaz de usuario
  isLoading: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';
  
  // URL base del backend de rentasCR
  private API_URL = (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1')
    ? 'https://argoappswebpage.herokuapp.com/api/auth'
    : 'http://localhost:1010/api/auth';
  private authToken: string = '';
  private userId: string = '';

  constructor(private http: HttpClient) { }

  /**
   * Ciclo de vida OnInit: Desplaza la vista al inicio de la página al cargar el componente.
   */
  ngOnInit(): void {
    window.scrollTo(0, 0);
  }

  /**
   * Envía las credenciales (correo y contraseña) al backend para solicitar la generación 
   * y envío del código de 6 dígitos de eliminación de cuenta al correo electrónico.
   */
  onSubmitLogin(): void {
    this.errorMessage = '';
    if (!this.email || !this.email.trim()) {
      this.errorMessage = 'Por favor ingresa tu correo electrónico.';
      return;
    }
    if (!this.password) {
      this.errorMessage = 'Por favor ingresa tu contraseña.';
      return;
    }

    this.isLoading = true;

    // Llamada al endpoint backend de solicitud de código de eliminación
    this.http.post<any>(`${this.API_URL}/request-delete-code`, {
      email: this.email.trim(),
      password: this.password
    }).subscribe({
      next: (res) => {
        this.isLoading = false;
        const isOk = res && (res.did !== false && res.success !== false);
        if (isOk) {
          const resData = res.res || res.data;
          if (resData && resData.token) {
            this.authToken = resData.token;
            this.userId = resData.usuario?._id || '';
          }
          this.step = 2;
          this.errorMessage = '';
          this.focusFirstDigitInput();
        } else {
          this.errorMessage = res?.msg || res?.message || 'Credenciales inválidas. Por favor verifica tu correo y contraseña.';
        }
      },
      error: (err) => {
        this.isLoading = false;
        const serverMsg = err?.error?.msg || err?.error?.message;
        if (serverMsg) {
          this.errorMessage = serverMsg;
        } else {
          this.errorMessage = 'Error de conexión con el servidor. Por favor intenta más tarde.';
        }
      }
    });
  }

  /**
   * Enfoca automáticamente la primera casilla del código de 6 dígitos al ingresar al paso 2.
   */
  private focusFirstDigitInput(): void {
    setTimeout(() => {
      const firstInput = document.getElementById('digit-0');
      if (firstInput) {
        firstInput.focus();
      }
    }, 100);
  }

  /**
   * Gestiona la entrada de dígitos en el formulario de 6 casillas.
   * Filtra solo números, asigna el valor al arreglo `codeDigits`, soporta pegar múltiples dígitos
   * y desplaza automáticamente el foco a la siguiente casilla.
   * 
   * @param event Evento de entrada de datos en el elemento HTML.
   * @param index Índice de la casilla actual (0 a 5).
   */
  /**
   * Gestiona el evento KeyUp para capturar el carácter ingresado y avanzar el foco
   * de forma segura una vez liberada la tecla, evitando duplicación en casillas subsecuentes.
   * 
   * @param event Evento de teclado de tipo KeyUp.
   * @param index Índice de la casilla actual (0 a 5).
   */
  onKeyUp(event: KeyboardEvent, index: number): void {
    const key = event.key;
    
    // Ignorar teclas de navegación que son manejadas por onKeyDown
    if (['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Shift'].includes(key)) {
      return;
    }

    const inputEl = event.target as HTMLInputElement;
    const rawVal = inputEl.value || '';
    const digitsOnly = rawVal.replace(/[^0-9]/g, '');

    if (digitsOnly.length > 1) {
      const chars = digitsOnly.split('');
      for (let i = 0; i < 6 && (index + i) < 6; i++) {
        this.codeDigits[index + i] = chars[i] || '';
        const el = document.getElementById(`digit-${index + i}`) as HTMLInputElement;
        if (el) el.value = this.codeDigits[index + i];
      }
      const nextIdx = Math.min(index + chars.length, 5);
      const nextEl = document.getElementById(`digit-${nextIdx}`) as HTMLInputElement;
      if (nextEl) nextEl.focus();
      return;
    }

    if (digitsOnly) {
      const char = digitsOnly.slice(-1);
      this.codeDigits[index] = char;
      inputEl.value = char;

      if (index < 5) {
        const nextInput = document.getElementById(`digit-${index + 1}`) as HTMLInputElement;
        if (nextInput) {
          nextInput.focus();
          nextInput.select();
        }
      }
    } else {
      this.codeDigits[index] = '';
      inputEl.value = '';
    }
  }

  /**
   * Maneja eventos de teclado KeyDown (Backspace, Flechas izquierda/derecha).
   * 
   * @param event Evento de teclado presionado.
   * @param index Índice de la casilla actual (0 a 5).
   */
  onKeyDown(event: KeyboardEvent, index: number): void {
    const inputEl = event.target as HTMLInputElement;
    if (event.key === 'Backspace') {
      if (!inputEl.value && index > 0) {
        this.codeDigits[index - 1] = '';
        const prevInput = document.getElementById(`digit-${index - 1}`) as HTMLInputElement;
        if (prevInput) {
          prevInput.value = '';
          prevInput.focus();
        }
      } else {
        this.codeDigits[index] = '';
        inputEl.value = '';
      }
    } else if (event.key === 'ArrowLeft' && index > 0) {
      const prevInput = document.getElementById(`digit-${index - 1}`) as HTMLInputElement;
      if (prevInput) prevInput.focus();
    } else if (event.key === 'ArrowRight' && index < 5) {
      const nextInput = document.getElementById(`digit-${index + 1}`) as HTMLInputElement;
      if (nextInput) nextInput.focus();
    }
  }

  /**
   * Permite pegar un código completo de 6 dígitos desde el portapapeles en cualquiera de las casillas.
   * 
   * @param event Evento de pegado de portapapeles.
   */
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const clipboardData = event.clipboardData || (window as any).clipboardData;
    const pastedText = clipboardData ? clipboardData.getData('text') : '';
    const digits = pastedText.replace(/[^0-9]/g, '');

    if (digits) {
      for (let i = 0; i < 6; i++) {
        const val = digits[i] || '';
        this.codeDigits[i] = val;
        const el = document.getElementById(`digit-${i}`) as HTMLInputElement;
        if (el) el.value = val;
      }
      const focusIndex = Math.min(digits.length, 6) - 1;
      const targetEl = document.getElementById(`digit-${Math.max(0, focusIndex)}`) as HTMLInputElement;
      if (targetEl) targetEl.focus();
    }
  }

  /**
   * Obtiene la cadena concatenada completa de los 6 dígitos del código de confirmación.
   */
  get fullCode(): string {
    return this.codeDigits.join('').trim();
  }

  /**
   * Envía el código de confirmación de 6 dígitos al backend para verificar la propiedad de la cuenta,
   * ejecutar el borrado definitivo de todos los datos y forzar el cierre de sesión en los dispositivos.
   */
  onSubmitCode(): void {
    this.errorMessage = '';
    const code = this.fullCode;
    if (code.length !== 6) {
      this.errorMessage = 'Por favor ingresa los 6 dígitos del código enviado a tu correo.';
      return;
    }

    this.isLoading = true;

    this.http.post<any>(`${this.API_URL}/confirm-delete-account`, {
      email: this.email.trim(),
      code: code,
      motivo: 'Solicitud de eliminación de cuenta desde página web',
      detalle: 'Confirmado mediante código de verificación enviado al correo'
    }).subscribe({
      next: (res) => {
        this.isLoading = false;
        const isOk = res && (res.did !== false && res.success !== false);
        if (isOk) {
          this.step = 3;
          this.successMessage = res.msg || res.message || 'Tu cuenta y datos han sido eliminados correctamente.';
        } else {
          this.errorMessage = res?.msg || res?.message || 'Código inválido o expirado.';
        }
      },
      error: (err) => {
        this.isLoading = false;
        const serverMsg = err?.error?.msg || err?.error?.message;
        if (serverMsg) {
          this.errorMessage = serverMsg;
        } else {
          this.errorMessage = 'Error al verificar el código de confirmación. Inténtalo nuevamente.';
        }
      }
    });
  }

  /**
   * Solicita el reenvío de un nuevo código de confirmación de 6 dígitos al correo electrónico del usuario.
   */
  resendCode(): void {
    this.errorMessage = '';
    this.successMessage = '';
    this.isLoading = true;
    this.http.post<any>(`${this.API_URL}/resend-delete-code`, { email: this.email.trim() }).subscribe({
      next: (res) => {
        this.isLoading = false;
        const isOk = res && (res.did !== false && res.success !== false);
        if (isOk) {
          this.successMessage = 'Un nuevo código de 6 dígitos ha sido enviado a tu correo electrónico.';
        } else {
          this.errorMessage = res?.msg || res?.message || 'Error al reenviar el código.';
        }
      },
      error: (err) => {
        this.isLoading = false;
        const serverMsg = err?.error?.msg || err?.error?.message;
        if (serverMsg) {
          this.errorMessage = serverMsg;
        } else {
          this.errorMessage = 'Error al enviar el nuevo código. Intenta nuevamente.';
        }
      }
    });
  }
}
